import { FC, useMemo, memo, useState, useCallback, useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { Accordion } from '@shared/ui/accordion';
import { SegmentCategoryHeader } from '@features/segments-category';
import { Category } from '@pages/event/api/useFetchCategory';
import { ParticipantCardNoSegment } from '@features/participant-card-no-segment';
import { useFetchTicketQr } from '@pages/event/api/useFetchTicketQr';
import { InvitedParticipants } from '@widgets/segments-tab';
import { VirtualizedParticipantsList } from '@features/segment-card/ui/virtualized-participants-list';
import { CategorySearchInput } from './ui/category-search-input';
import { EventSettings } from '@features/api/use-get-settings.ts';

const VIRTUALIZATION_THRESHOLD = 50;

interface SegmentCardCategoryProps {
  category: Category;
  refetch: () => void;
  eventId: number;
  selectedIds: number[];
  setSelectedParticipants: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedIds: (newIds: number[]) => void;
  handleDeleteTickets: (categoryId: number, ticketIds: number[]) => Promise<void>;
  refetchSegments?: () => void;
  refetchEvent?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
  isCBGroup?: boolean;
  eventSettings?: EventSettings | null;
}

export const SegmentCardCategory: FC<SegmentCardCategoryProps> = memo(
  ({
    category,
    refetch,
    setSelectedIds,
    selectedIds,
    handleDeleteTickets,
    eventId,
    setSelectedParticipants,
    refetchSegments,
    refetchEvent,
    eventDateEnd,
    eventDateStart,
    isCBGroup = false,
    eventSettings,
  }) => {
    const [filterSearch, setFilterSearch] = useState('');
    const [currentTicketId, setCurrentTicketId] = useState(0);
    const lastDownloadedRef = useRef<number | null>(null);

    const { data } = useFetchTicketQr(currentTicketId);

    const getQrCodeFileName = (userName: string, userSurname: string, ticketId: number): string => {
      return `qr_code_${userName}_${userSurname}_${ticketId}.svg`;
    };

    useEffect(() => {
      if (!data || !currentTicketId) return;
      if (lastDownloadedRef.current === currentTicketId) return;
      const userName = data.getTicketQrcodeById.firstName || '';
      const userSurname = data.getTicketQrcodeById.lastName || '';
      const a = document.createElement('a');
      document.body.appendChild(a);
      const blob = new Blob([data.getTicketQrcodeById.qr], { type: 'image/svg+xml' });
      const file = window.URL.createObjectURL(blob);
      a.href = file;
      a.download = getQrCodeFileName(userName, userSurname, currentTicketId);
      a.click();
      lastDownloadedRef.current = currentTicketId;
    }, [data, currentTicketId]);

    const toggleCheckbox = (ticketId: number, participantId: number) => {
      const currentIds = selectedIds;
      const newIds = currentIds.includes(ticketId)
        ? currentIds.filter((id) => id !== ticketId)
        : [...currentIds, ticketId];

      setSelectedIds(newIds);

      setSelectedParticipants((prev) =>
        prev.includes(participantId) ? prev.filter((id) => id !== participantId) : [...prev, participantId]
      );
    };

    const filteredTickets = useMemo(() => {
      const searchLower = filterSearch.toLowerCase().trim();
      if (!searchLower) return category.tickets;

      return category.tickets.filter((ticket) => {
        const firstName = ticket.participant.firstName?.toLowerCase().trim();
        const lastName = ticket.participant.lastName?.toLowerCase().trim();
        const fullName = `${firstName} ${lastName}`;
        const reversedName = `${lastName} ${firstName}`;

        return (
          fullName?.includes(searchLower) ||
          reversedName?.includes(searchLower) ||
          firstName?.includes(searchLower) ||
          lastName?.includes(searchLower)
        );
      });
    }, [filterSearch, category.tickets]);

    const regularParticipants = useMemo(
      () => filteredTickets.filter((ticket) => ticket.participant?.position !== null),
      [filteredTickets]
    );

    const freelanceParticipants = useMemo(
      () => filteredTickets.filter((ticket) => ticket.participant?.position === null),
      [filteredTickets]
    );

    const participantsIds = useMemo(() => {
      return category.tickets.map((ticket) => ticket.participant.id);
    }, [category.tickets]);

    const invitedParticipants: InvitedParticipants[] = useMemo(() => {
      return category.tickets.map((ticket) => ({
        userId: ticket.participant.id,
        ticketId: ticket.id,
      }));
    }, [category.tickets]);

    const eligibleTickets = useMemo(() => category.tickets.filter((t) => t.participant?.active), [category.tickets]);

    const isAllSelected = eligibleTickets.length > 0 && eligibleTickets.every((t) => selectedIds.includes(t.id));
    const handleSelectAll = (isSelected: boolean) => {
      const tickets = isSelected ? eligibleTickets : [];
      const allTicketIds = tickets.map((t) => t.id);
      const allParticipantIds = tickets.map((t) => t.participant.id);

      setSelectedIds(allTicketIds);
      setSelectedParticipants((prev) =>
        isSelected
          ? [...new Set([...prev, ...allParticipantIds])]
          : prev.filter((id) => !allParticipantIds.includes(id))
      );
    };

    const handleSearchChange = useCallback((value: string) => {
      setFilterSearch(value);
    }, []);

    const renderParticipantCard = useCallback(
      (ticket: (typeof regularParticipants)[0], isFreelance = false) => {
        const isDisabled = !ticket.participant?.active;
        const position = isFreelance && isDisabled ? 'Больше не работает' : ticket.participant.position ?? undefined;
        const name = `${ticket.participant.firstName} ${ticket.participant.lastName}`;
        return (
          <div {...stylex.props(styles.row)}>
            <ParticipantCardNoSegment
              ticket={ticket}
              position={position}
              name={name}
              checked={selectedIds.includes(ticket.id)}
              disabled={isDisabled}
              onToggle={() => toggleCheckbox(ticket.id, ticket.participant.id)}
              onDelete={() => handleDeleteTickets(category.id, [ticket.id])}
              getTicketQR={() => setCurrentTicketId(ticket.id)}
              eventId={eventId}
              refetchSegments={refetchSegments}
              refetchCategories={refetch}
              eventDateEnd={eventDateEnd}
              eventDateStart={eventDateStart}
              eventSettings={eventSettings}
            />
          </div>
        );
      },
      [
        selectedIds,
        toggleCheckbox,
        handleDeleteTickets,
        category.id,
        eventId,
        refetchSegments,
        refetch,
        eventDateEnd,
        eventDateStart,
        eventSettings,
      ]
    );

    return (
      <Accordion
        element={
          <SegmentCategoryHeader
            participantsIds={participantsIds}
            invitedParticipants={invitedParticipants}
            title={category.title}
            description={category.description}
            countTicket={category.countTicket}
            categoryId={category.id}
            refetch={() => {
              refetch();
              refetchSegments?.();
              refetchEvent?.();
            }}
            eventId={eventId}
            onSelectAll={handleSelectAll}
            checked={isAllSelected}
            color={category.color}
            category={category}
            isCBGroup={isCBGroup}
          />
        }
        onToggle={() => {}}
      >
        <div {...stylex.props(styles.input)}>
          <CategorySearchInput onSearchChange={handleSearchChange} />
        </div>
        {regularParticipants.length > 0 && (
          <>
            <div {...stylex.props(styles.navText)}>
              <p>Участники мероприятия</p>
            </div>

            <div {...stylex.props(styles.column)}>
              {regularParticipants.length >= VIRTUALIZATION_THRESHOLD ? (
                <VirtualizedParticipantsList
                  items={regularParticipants}
                  renderItem={renderParticipantCard}
                  scrollResetKey={filterSearch}
                />
              ) : (
                regularParticipants.map((ticket) => <div key={ticket.id}>{renderParticipantCard(ticket)}</div>)
              )}
            </div>
          </>
        )}
        {freelanceParticipants.length > 0 && (
          <>
            <div {...stylex.props(styles.navText)}>
              <p>Внештатные участники</p>
            </div>

            <div {...stylex.props(styles.column)}>
              {freelanceParticipants.map((ticket) => (
                <div key={ticket.id}>{renderParticipantCard(ticket, true)}</div>
              ))}
            </div>
          </>
        )}
      </Accordion>
    );
  }
);
